import { useCallback, useMemo, useState, useEffect } from 'react';
import moment from 'moment';
import Button from 'antd/es/button';
import momentTimeZone from 'moment-timezone';
import DatePicker from 'antd/es/date-picker';

import { DateFormat } from 'utils/constants';
import { useAppSelector } from 'utils/hooks';
import { Header6 } from 'components/layout/headers';
import { getCourseSelector } from 'models/course/selectors';
import { generateCalendarWeekDays } from 'modules/api-requests/weather-data/constants';
import {
  CalendarDay,
  TabPane,
  WeatherDetails,
  WeatherIcon,
  WeatherTab,
  WeekSelectorContainer,
  CalendarLeftExtraContent,
} from './elements';
import { BookingCalendarIcon } from 'pages/booking/booking-details-page/booking-info-panel/icons/booking-calendar-icon';

import type { Moment } from 'moment/moment';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';

interface IWeatherCalendarProps {
  value: Moment | null | undefined;
  onChange?: (key: string) => void;
  weatherList?: Array<{
    date: string;
    temp: number;
    description: string;
    icon: string;
  }>;
}

const SCROLL_STEP = 400;

export const getConditionIcon = (imageName = '01d') => `/images/condition-icons/${imageName}@2x.png`;

export const WeatherCalendar = ({ onChange, value, weatherList }: IWeatherCalendarProps) => {
  const currentCourse = useAppSelector(getCourseSelector);
  const [openedPicker, setOpenedPicker] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [maxScrollPosition, setMaxScrollPosition] = useState(0);

  const toggleOpenedPicker = useCallback(() => {
    setDisabled(true);
    setTimeout(() => setDisabled(false), 200);
    setOpenedPicker(false);
  }, []);

  const onManualClickTrigger = useCallback(() => {
    if (!disabled) {
      setOpenedPicker((value) => !value);
    }
  }, [disabled]);

  const onDataPickerChange = useCallback(
    (value: Moment | null) => {
      if (value) {
        if (onChange) {
          onChange(String(value.format(DateFormat)));
        }
      }
    },
    [onChange]
  );

  const disabledDate = useCallback(
    (date: Moment) => {
      const dateTz = date.tz(currentCourse?.timeZone || momentTimeZone.tz.guess());
      const leftDate = moment()
        .tz(currentCourse?.timeZone || momentTimeZone.tz.guess())
        .subtract(1, 'days')
        .endOf('day');
      const rightDate = moment()
        .tz(currentCourse?.timeZone || momentTimeZone.tz.guess())
        .add(currentCourse?.bookingAllowedDays ?? 14, 'days')
        .endOf('day');
      return dateTz < leftDate || dateTz > rightDate;
    },
    [currentCourse]
  );

  const onTabScroll = useCallback(() => {
    const wrapper = document.querySelector('#weather-calendar .ant-tabs-nav-wrap') as HTMLElement;
    const list = document.querySelector('#weather-calendar .ant-tabs-nav-list') as HTMLElement;
    const wrapperWidth = wrapper.clientWidth;
    const listWidth = list.clientWidth;
    const maxScroll = listWidth - wrapperWidth;
    const currentScroll = Math.abs(
      Number(list.style.transform.split(',')[0].replace('translate(', '').replace('px', ''))
    );
    setScrollPosition(currentScroll);
    setMaxScrollPosition(maxScroll);
  }, [setScrollPosition, setMaxScrollPosition]);

  const onScrollRightClick = useCallback(() => {
    onTabScroll();
    if (scrollPosition < maxScrollPosition) {
      const list = document.querySelector('#weather-calendar .ant-tabs-nav-list') as HTMLElement;
      const newPosition = Math.min(maxScrollPosition, scrollPosition + SCROLL_STEP);
      list.style.transform = `translate(-${newPosition}px, 0px)`;
    }
    onTabScroll();
  }, [onTabScroll, scrollPosition, maxScrollPosition]);

  const onScrollLeftClick = useCallback(() => {
    onTabScroll();
    if (scrollPosition > 0) {
      const list = document.querySelector('#weather-calendar .ant-tabs-nav-list') as HTMLElement;
      const newPosition = Math.max(0, scrollPosition - SCROLL_STEP);
      list.style.transform = `translate(-${newPosition}px, 0px)`;
    }
    onTabScroll();
  }, [onTabScroll, scrollPosition]);

  const onTabChange = (activeKey: string) => {
    if (onChange) {
      onChange(activeKey);
    }
  };

  useEffect(() => {
    if (maxScrollPosition === 0) {
      onTabScroll();
    }
  }, [onTabScroll, maxScrollPosition]);

  const extraContent = useMemo(
    () => ({
      right: (
        <Button
          type="text"
          className="calendar-arrow-button calendar-arrow-button-right"
          disabled={scrollPosition > maxScrollPosition - 20}
          onClick={onScrollRightClick}
        >
          <RightOutlined />
        </Button>
      ),
      left: (
        <CalendarLeftExtraContent>
          <WeekSelectorContainer>
            <BookingCalendarIcon onClick={onManualClickTrigger} />
            <span onClick={onManualClickTrigger}>Select A Date</span>

            <DatePicker
              disabled={disabled}
              value={value}
              open={openedPicker}
              allowClear={false}
              className="calendar-date-selector"
              onOpenChange={toggleOpenedPicker}
              disabledDate={disabledDate}
              onChange={onDataPickerChange}
            />
          </WeekSelectorContainer>
          <Button
            type="text"
            className="calendar-arrow-button calendar-arrow-button-left"
            disabled={scrollPosition < 20}
            onClick={onScrollLeftClick}
          >
            <LeftOutlined />
          </Button>
        </CalendarLeftExtraContent>
      ),
    }),
    [
      onManualClickTrigger,
      disabled,
      value,
      openedPicker,
      toggleOpenedPicker,
      disabledDate,
      onDataPickerChange,
      onScrollLeftClick,
      onScrollRightClick,
      scrollPosition,
      maxScrollPosition,
    ]
  );

  const generatedCalendar = useMemo(() => {
    const leftDate = moment()
      .tz(currentCourse?.timeZone || momentTimeZone.tz.guess())
      .startOf('day');
    const rightDate = moment()
      .tz(currentCourse?.timeZone || momentTimeZone.tz.guess())
      .add(currentCourse?.bookingAllowedDays ?? 14, 'days')
      .endOf('day');

    return generateCalendarWeekDays(leftDate, rightDate).map((weather) => {
      const dayMoment = weather.day;
      const foundWeather = weatherList?.find((item) => item.date === dayMoment.format(DateFormat));
      return (
        <TabPane
          tab={
            <>
              <CalendarDay>{dayMoment.format('dddd')}</CalendarDay>
              <WeatherDetails>
                <Header6>{dayMoment.format('MMM D')}</Header6>

                {foundWeather && (
                  <>
                    <WeatherIcon title={foundWeather.description} $url={getConditionIcon(foundWeather.icon)} />
                    <Header6 title={foundWeather.description}>{foundWeather.temp}°F</Header6>
                  </>
                )}
              </WeatherDetails>
            </>
          }
          key={String(dayMoment.format(DateFormat))}
        />
      );
    });
  }, [currentCourse?.timeZone, currentCourse?.bookingAllowedDays, weatherList]);

  return (
    <WeatherTab
      id="weather-calendar"
      onChange={onTabChange}
      tabBarExtraContent={extraContent}
      onTabScroll={onTabScroll}
      activeKey={String(value ? value.format(DateFormat) : undefined)}
    >
      {generatedCalendar}
    </WeatherTab>
  );
};

